import { deleteCart } from '@/api/cart';
import { fetchMeetConditionsApi } from '@/api/public';
import { ICartGroup, ICartItem } from '@/api/types/cart';
import { fetchAddWish } from '@/api/wish';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { toast } from '@/components/Toast';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { EPlatform } from '@/utils/enums/platform-enum';
import { useTranslations } from '@/i18n/use-translations';
import { useRouter } from 'next/navigation';
import { useCallback, useContext, useState } from 'react';
import { useCartSensorsTracking } from './useCartSensorsTracking';
interface UseCartActionsProps {
  cartCanBuyList: ICartGroup[];
  cartCanNotBuyList: ICartItem[];
  setShowCartItemDialog: (show: boolean) => void;
  setCartItemDialogProps: (props: any) => void;
  reloadCart: () => Promise<void>;
  // 状态管理回调
  setCartCanBuyList: (list: ICartGroup[]) => void;
  setCartCanNotBuyList: (list: ICartItem[]) => void;
  setAllCheck: (checked: boolean) => void;
  token: string;
  setShowLoginRegisterDialog: (config: {
    show: boolean;
    eventType?: string;
    situation?: string;
    initiative?: boolean;
  }) => void;
  getItemPromotion?: (cartId: number) => any;
}

export const useCartActions = ({
  cartCanBuyList,
  cartCanNotBuyList,
  setShowCartItemDialog,
  setCartItemDialogProps,
  reloadCart,
  setCartCanBuyList,
  setCartCanNotBuyList,
  setAllCheck,
  token,
  setShowLoginRegisterDialog,
  getItemPromotion,
}: UseCartActionsProps) => {
  const accountT = useTranslations('account.recentlyViewed.deleteConfirmation');
  const t = useTranslations('cart.hooks');
  const router = useRouter();
  const { deviceType } = useContext(ResponsiveContext);

  // 埋点相关Hook
  const { trackCartItemBtnClick, trackCollectBtnClick, trackCollectClick } =
    useCartSensorsTracking();

  // 批量操作状态 - 移到 hook 内部管理
  const [batchFlag, setBatchFlag] = useState(false);
  const getWish = useStore((state) => state.getWish);

  // 计算批量操作是否禁用
  const batchDisabled =
    !cartCanBuyList.some((item) => item.selected) &&
    !cartCanNotBuyList.some((item) => item.selected);

  // 批量收藏
  const handleBatchCollect = useCallback(async () => {
    if (batchDisabled) return;

    if (!token) {
      setShowLoginRegisterDialog({
        show: true,
        eventType: 'collect',
        situation: '收藏',
      });
      return;
    }

    const selectedProductIds: number[] = [];
    const selectedInvalidProductIds: number[] = [];
    const selectedProducts: any[] = [];

    // 收集选中的可购买商品的 product_id
    cartCanBuyList.forEach((item) => {
      if (item.selected && item.carts) {
        item.carts.forEach((cart) => {
          if (cart.product_id) {
            selectedProductIds.push(cart.product_id);
            selectedProducts.push({
              product_id: cart.product_id,
              shop_sku: cart.product?.shop_sku,
              business_sku: (cart.product as any)?.business_sku,
              product_sku: (cart.product as any)?.sku,
              product_name: cart.product?.short_name,
            });

            // 🔍 发送神策埋点 - 收藏按钮点击和收藏点击（批量操作）
            trackCollectBtnClick(cart as any, true); // isBatchAction = true
            trackCollectClick(cart as any);
          }
        });
      }
    });

    // 收集选中的无效商品的 product_id
    cartCanNotBuyList.forEach((item) => {
      if (item.selected && item.product_id) {
        selectedInvalidProductIds.push(item.product_id);
        selectedProducts.push({
          product_id: item.product_id,
          shop_sku: item.product?.shop_sku,
          business_sku: (item.product as any)?.business_sku,
          product_sku: (item.product as any)?.sku,
          product_name: item.product?.short_name,
        });

        // 🔍 发送神策埋点 - 收藏按钮点击和收藏点击（批量操作）
        trackCollectBtnClick(item as any, true); // isBatchAction = true
        trackCollectClick(item as any);
      }
    });

    const allProductIds = [...selectedProductIds, ...selectedInvalidProductIds];

    if (allProductIds.length === 0) {
      return;
    }

    try {
      // 调用收藏API
      const response = await fetchAddWish({ product_id: allProductIds });
      if (response.code === 200) {
        toast.success(t('successAddToWishlist'));

        // 更新被收藏商品的 is_wish 状态
        const updatedCanBuyList = cartCanBuyList.map((item) => {
          if (item.selected && item.carts) {
            const updatedCarts = item.carts.map((cart) => {
              if (cart.product_id && allProductIds.includes(cart.product_id)) {
                return {
                  ...cart,
                  product: {
                    ...cart.product,
                    is_wish: 1, // 设置为已收藏状态
                  },
                };
              }
              return cart;
            });
            return { ...item, carts: updatedCarts };
          }
          return item;
        });

        const updatedCanNotBuyList = cartCanNotBuyList.map((item) => {
          if (item.selected && item.product_id && allProductIds.includes(item.product_id)) {
            return {
              ...item,
              product: {
                ...item.product,
                is_wish: 1, // 设置为已收藏状态
              },
            };
          }
          return item;
        });

        // 更新心愿单数量
        getWish();

        // 更新购物车列表状态
        setCartCanBuyList(updatedCanBuyList);
        setCartCanNotBuyList(updatedCanNotBuyList);

        // WAP端跳转到app引流页面
        if (deviceType === EPlatform.WAP) {
          try {
            const meetConditionsRes = await fetchMeetConditionsApi({ condition_2: 3 });
            if (meetConditionsRes.code === 200 && meetConditionsRes.data.is_meet_conditions) {
              router.push(`/activity-page?id=${meetConditionsRes.data.active_page_id}`);
            }
          } catch (error) {
            logger.warn('Meet conditions check failed:', error);
          }
        }
      }
    } catch (error) {
      logger.error('Failed to add to wishlist:', error);
      toast.error(t('failedToAddToWishlist'));
    }
  }, [
    cartCanBuyList,
    cartCanNotBuyList,
    batchDisabled,
    deviceType,
    router,
    setCartCanBuyList,
    setCartCanNotBuyList,
    trackCollectBtnClick, // 添加埋点依赖
    trackCollectClick, // 添加埋点依赖
  ]);

  // 批量删除
  const handleBatchDelete = useCallback(() => {
    if (batchDisabled) return;

    const selectedGiftItems: ICartGroup[] = [];
    const selectedCanBuyIds: number[] = [];
    const selectedCanNotBuyIds: number[] = [];

    // 收集选中的可购买商品的 cart_id
    cartCanBuyList.forEach((item) => {
      if (item.selected && item.carts) {
        item.carts.forEach((cart) => {
          if (cart.cart_id) {
            selectedCanBuyIds.push(cart.cart_id);
          }
        });
        // 检查是否包含赠品
        if (item.type === 4) {
          selectedGiftItems.push(item);
        }
      }
    });

    // 收集选中的无效商品的 cart_id
    cartCanNotBuyList.forEach((item) => {
      if (item.selected && item.cart_id) {
        selectedCanNotBuyIds.push(item.cart_id);
      }
    });

    const allSelectedIds = [...selectedCanBuyIds, ...selectedCanNotBuyIds];

    if (allSelectedIds.length === 0) {
      return;
    }

    // 设置确认对话框内容
    let content = '';
    let content2 = allSelectedIds.length > 1 ? accountT('message') : t('deleteItemConfirm');
    let content2TextAlignCenter = false;

    // 如果包含赠品，显示特殊提示
    if (selectedGiftItems.length > 0) {
      content = t('removeGiftConfirm');
      content2 = t('removeGiftTip');
      content2TextAlignCenter = true;
    }

    setCartItemDialogProps({
      content,
      content2,
      content2TextAlignCenter,
      confirmText: t('delete'),
      cancelText: t('cancel'),
      onConfirm: () => performBatchDelete(allSelectedIds),
    });
    setShowCartItemDialog(true);
  }, [
    cartCanBuyList,
    cartCanNotBuyList,
    batchDisabled,
    setCartItemDialogProps,
    setShowCartItemDialog,
  ]);

  // 执行批量删除
  const performBatchDelete = useCallback(
    async (cartIds: number[]) => {
      try {
        // 🔍 发送神策埋点 - 批量删除前，为每个选中的商品发送埋点
        cartCanBuyList.forEach((item) => {
          if (item.selected && item.carts) {
            item.carts.forEach((cart) => {
              if (cartIds.includes(cart.cart_id)) {
                trackCartItemBtnClick(cart as any, 'item删除', {
                  selected: true,
                  itemPromotion: getItemPromotion?.(cart.cart_id || 0) ?? null,
                });
              }
            });
          }
        });

        cartCanNotBuyList.forEach((item) => {
          if (item.selected && item.cart_id && cartIds.includes(item.cart_id)) {
            // 不可结算商品勾选也不会重新调取计价，不会有itemPromotion
            trackCartItemBtnClick(item as any, 'item删除', {
              selected: false,
              itemPromotion: null,
            });
          }
        });

        await deleteCart({ cart_ids: cartIds });
        await reloadCart();

        // 判断购物车条目是否为空，如果为空，关闭管理模式
        if (cartCanBuyList.length === 0) {
          setBatchFlag(false);
        }
        toast.success(t('itemsDeletedSuccessfully'));
        setShowCartItemDialog(false);
      } catch (error) {
        logger.error('Failed to delete items:', error);
        toast.error(t('failedToDeleteItems'));
      }
    },
    [
      cartCanBuyList,
      cartCanNotBuyList,
      setBatchFlag,
      setShowCartItemDialog,
      reloadCart,
      trackCartItemBtnClick,
      getItemPromotion,
    ],
  );

  // 取消批量模式
  const handleBatchCancel = useCallback(() => {
    setBatchFlag(false);
    setAllCheck(false);
  }, [setBatchFlag, setAllCheck]);

  // 清空所有无效商品
  const handleClearAll = useCallback(async () => {
    const invalidIds = cartCanNotBuyList.flatMap((item) => item.cart_id || []).filter(Boolean);

    if (invalidIds.length === 0) {
      return;
    }

    try {
      await deleteCart({ cart_ids: invalidIds });
      await reloadCart();
    } catch (error) {
      logger.error('Failed to clear invalid items:', error);
    }
  }, [cartCanNotBuyList, reloadCart]);

  // 别名方法（为了保持向后兼容）
  const moveWish = useCallback(() => {
    handleBatchCollect();
  }, [handleBatchCollect]);

  const clearAll = useCallback(() => {
    handleClearAll();
  }, [handleClearAll]);

  const batchCancel = useCallback(() => {
    handleBatchCancel();
  }, [handleBatchCancel]);

  return {
    // 状态
    batchFlag,
    setBatchFlag,
    batchDisabled,

    // 主要方法
    handleBatchCollect,
    handleBatchDelete,
    handleClearAll,
    handleBatchCancel,
    performBatchDelete,

    // 别名方法（向后兼容）
    moveWish,
    clearAll,
    batchCancel,
  };
};
